/**
 * SupplyVue design tokens — shared by the Planner, Scheduler and Modelling portals.
 *
 * Copied verbatim from the Planner Portal's `src/design/tokens.ts`
 * (Planner repo, branch feature/US149194-portal-visual-baseline). Keep the
 * copies identical: change the Planner's first, then copy it here.
 *
 * This file EXTENDS `SchedulerPortal/src/features/Scheduler/SchedulerDesign.tsx`.
 * Every key that file exports keeps its name and (with the two exceptions noted
 * below) its value, so the Scheduler can swap its local module for this one
 * without touching a call site. New keys are marked ADDED.
 *
 *   TOKENS   — colour
 *   TYPE     — typographic styles as `sx` fragments
 *   RADII    — corner radii
 *   SHADOWS  — elevation
 *   SIZES    — ADDED: the fixed dimensions the layout depends on
 *   SPACE    — ADDED: the 4px scale
 *   Z        — ADDED: z-index levels
 *   MOTION   — ADDED: durations and easing (tokens.json has no motion family,
 *              so this object and tokens.css are the only home for them)
 *
 * The CSS custom property for any colour is the kebab-cased name:
 * `TOKENS.brandTeal` is `var(--brand-teal)`. Prefer the variable in CSS and the
 * object in TypeScript; do not inline the hex anywhere.
 *
 * TWO DELIBERATE CHANGES from SchedulerDesign.tsx, both argued in Decisions:
 *   · `sidebar` is now an alias of `panel` (#FAFBFC). It was #f8fafc — two units
 *     apart, and a fifth near-identical light grey.
 *   · Chrome accents use `brandTeal`. `stateComplete` is a status colour only.
 *     The Scheduler currently uses it as the general accent in 81 places.
 */

export const FONT_SANS =
  '"Archivo Variable", Archivo, system-ui, -apple-system, sans-serif'
export const FONT_MONO =
  '"JetBrains Mono Variable", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace'
/** Back-compat alias used by the Scheduler's timeline primitives module. */
export const MONO = FONT_MONO

export const TOKENS = {
  // ── brand ─────────────────────────────────────────────────────────────
  brandTeal: '#00415A',
  teal700: '#013247',
  teal300: '#4E7689',
  tealTint: '#ECF1F3',
  brandBlue: '#0082B3',
  blue600: '#006A93',
  blue50: '#E7F3F8',
  /** ADDED. The one focus indicator. 11.03:1 on surface0, 9.97:1 on panelHead. */
  focusRing: '#00415A',

  // ── ink ───────────────────────────────────────────────────────────────
  ink900: '#0E1B22',
  /** ADDED. The Scheduler kit referenced --ink-800 six times and never declared it. */
  ink800: '#1C2930',
  ink700: '#29373F',
  ink600: '#3C4A53',
  ink500: '#51616C',
  /** Icons and rules only — 4.07:1 on surface0, below the 4.5:1 text floor. */
  ink400: '#71808A',
  /** Disabled text and placeholders only — 2.55:1. */
  ink300: '#97A4AC',

  // ── line ──────────────────────────────────────────────────────────────
  /** ADDED. Every control border. 3.33:1 on surface0, 3.07:1 on surface50. */
  line400: '#7F8F9B',
  /** Decoration only — 1.57:1 on surface0. Never a control boundary. */
  line300: '#C5D0D7',
  line200: '#DBE2E7',
  line100: '#EAEFF2',

  // ── surface ───────────────────────────────────────────────────────────
  surface0: '#FFFFFF',
  surface50: '#F3F6F8',
  panel: '#FAFBFC',
  panelHead: '#F0F4F6',
  /** @deprecated Alias of `panel`. Kept so Scheduler call sites keep compiling. */
  sidebar: '#FAFBFC',
  /** ADDED. The one warning surface: needs input, or edited and unsaved. */
  surfaceWarn: '#FDF6E7',

  // ── state ─────────────────────────────────────────────────────────────
  stateComplete: '#0E7C86',
  /** ADDED. Chip fill. */
  stateCompleteTint: '#E7F2F3',
  /** ADDED. Text on stateCompleteTint (5.70:1). stateComplete is 4.33:1 there. */
  stateCompleteFg: '#0B6870',
  stateRunning: '#C77F2A',
  /** ADDED. Chip fill; carries amberFg (4.83:1). */
  stateRunningTint: '#F8F0E5',
  stateFailed: '#C44536',
  /** ADDED. Chip fill. */
  stateFailedTint: '#F9ECEB',
  /** ADDED. Text on stateFailedTint (5.73:1). */
  stateFailedFg: '#A6362A',
  statePending: '#97A4AC',
  /** ADDED. Amber chip fill. */
  amberTint: '#FBF2E6',
  amberFg: '#9A5A12',

  // ── Gantt (Scheduler; shipped here so one file serves both portals) ────
  ganttProduction: '#0082B3',
  /** 2.52:1 on surface0 — fails the 3:1 mark floor. Inherited exactly; always legend it. */
  ganttClean: '#E0922F',
  ganttPreClean: '#F1C684',
  ganttMaxRunClean: '#8E6FCE',
  ganttChangeover: '#8595A4',
  ganttDowntime: '#C44536',
  ganttIdle: '#EDF1F4',

  // ── charts (ADDED) ────────────────────────────────────────────────────
  /** Categorical. Assigned in order, never cycled. A ninth series folds into Other. */
  chartCat1: '#0082B3',
  chartCat2: '#B9761C',
  chartCat3: '#C0508F',
  chartCat4: '#5C9E2E',
  chartCat5: '#8E6FCE',
  chartCat6: '#1B8A5A',
  chartCat7: '#3D4EA8',
  chartCat8: '#A8452C',
  /** Sequential, light to dark. Magnitude, never identity. */
  chartSeq1: '#7FC0DA',
  chartSeq2: '#46A0C2',
  chartSeq3: '#0B82AB',
  chartSeq4: '#006585',
  chartSeq5: '#00455C',
  /** Diverging. 1..3 negative, 4 the grey midpoint, 5..7 positive. */
  chartDiv1: '#A8452C',
  chartDiv2: '#BC6B4C',
  chartDiv3: '#D79A83',
  chartDiv4: '#DDE3E7',
  chartDiv5: '#7FC0DA',
  chartDiv6: '#2E8FB6',
  chartDiv7: '#00618A',
  chartGrid: '#EAEFF2',
  chartAxis: '#C5D0D7',
  chartInk: '#51616C',
} as const

/** The eight categorical slots, in assignment order. */
export const CHART_CATEGORICAL = [
  TOKENS.chartCat1,
  TOKENS.chartCat2,
  TOKENS.chartCat3,
  TOKENS.chartCat4,
  TOKENS.chartCat5,
  TOKENS.chartCat6,
  TOKENS.chartCat7,
  TOKENS.chartCat8,
] as const
export const CHART_SEQUENTIAL = [
  TOKENS.chartSeq1,
  TOKENS.chartSeq2,
  TOKENS.chartSeq3,
  TOKENS.chartSeq4,
  TOKENS.chartSeq5,
] as const
export const CHART_DIVERGING = [
  TOKENS.chartDiv1,
  TOKENS.chartDiv2,
  TOKENS.chartDiv3,
  TOKENS.chartDiv4,
  TOKENS.chartDiv5,
  TOKENS.chartDiv6,
  TOKENS.chartDiv7,
] as const

export const RADII = {
  r0: '0px',
  sm: '2px',
  md: '3px',
  /** ADDED. The switch track only — half its 18px height. */
  pill: '9px',
} as const

export const SHADOWS = {
  panel: '0 1px 2px rgba(14,27,34,0.06)',
  /** ADDED. A sticky header once the content beneath it scrolls. */
  sticky: '0 2px 4px rgba(14,27,34,0.08)',
  pop: '0 6px 22px rgba(14,27,34,0.16)',
} as const

/** ADDED. 4px base. `theme.spacing(n)` is `4 * n`, so SPACE[8] === theme.spacing(2). */
export const SPACE = {
  2: '2px',
  4: '4px',
  6: '6px',
  8: '8px',
  10: '10px',
  12: '12px',
  14: '14px',
  16: '16px',
  20: '20px',
  24: '24px',
  32: '32px',
  40: '40px',
} as const

/** ADDED. The fixed dimensions the shell and the grids depend on. */
export const SIZES = {
  rowH: 28,
  rowHComfortable: 36,
  gridHeaderH: 32,
  controlH: 32,
  controlHSm: 26,
  controlHXs: 20,
  brandBarH: 44,
  crumbBarH: 40,
  topStripH: 46,
  sidebarW: 230,
  sidebarWCollapsed: 48,
  panelW: 312,
  footerH: 60,
} as const

/** ADDED. */
export const Z = {
  sticky: 3,
  popover: 50,
  toast: 80,
  modal: 200,
  tooltip: 999,
} as const

/** ADDED. tokens.json has no motion family, so this object is the only home. */
export const MOTION = {
  /** Hover, border and colour changes on a control. */
  fast: '120ms',
  /** A switch knob, a caret rotation. */
  base: '140ms',
  /** A progress bar, a panel reveal. */
  slow: '300ms',
  easing: 'cubic-bezier(0.2, 0, 0, 1)',
} as const

const tnum = { fontVariantNumeric: 'tabular-nums' as const }

/**
 * Typographic styles as `sx` fragments, keeping the Scheduler's key names.
 * `title` is a new alias of `h2`; `display`, `sectionLabel`, `body`, `small`,
 * `micro`, `mono` and `data` are unchanged in name and role.
 */
export const TYPE = {
  display: {
    fontFamily: FONT_SANS,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.15,
    color: TOKENS.ink900,
  },
  h2: {
    fontFamily: FONT_SANS,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '-0.005em',
    lineHeight: 1.2,
    color: TOKENS.ink900,
  },
  /** ADDED. Alias of `h2` under the tokens.json name. */
  title: {
    fontFamily: FONT_SANS,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '-0.005em',
    lineHeight: 1.2,
    color: TOKENS.ink900,
  },
  /** ADDED. The step the Scheduler's ladder is missing between 22 and a 12px label. */
  subtitle: {
    fontFamily: FONT_SANS,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '-0.005em',
    lineHeight: 1.3,
    color: TOKENS.ink900,
  },
  sectionLabel: {
    fontFamily: FONT_SANS,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    lineHeight: 1.2,
    color: TOKENS.ink500,
  },
  body: {
    fontFamily: FONT_SANS,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.45,
  },
  /** ADDED. */
  bodyStrong: {
    fontFamily: FONT_SANS,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.45,
  },
  small: {
    fontFamily: FONT_SANS,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  micro: {
    fontFamily: FONT_SANS,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    lineHeight: 1.3,
    color: TOKENS.ink500,
  },
  /** ADDED. Buttons, tabs, menu items. */
  control: {
    fontFamily: FONT_SANS,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.01em',
    lineHeight: 1.2,
  },
  /** ADDED. */
  fieldLabel: {
    fontFamily: FONT_SANS,
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    lineHeight: 1.2,
    color: TOKENS.ink500,
  },
  /** ADDED. ink500, not ink400 — ink400 is 3.68:1 on panelHead. */
  columnHeader: {
    fontFamily: FONT_SANS,
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    lineHeight: 1.2,
    color: TOKENS.ink500,
  },
  /** ADDED. A KPI tile's number. */
  metricLg: {
    fontFamily: FONT_MONO,
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1,
    ...tnum,
  },
  /** ADDED. A metric in a panel header strip. */
  metric: {
    fontFamily: FONT_MONO,
    fontSize: 17,
    fontWeight: 600,
    lineHeight: 1,
    ...tnum,
  },
  /** ADDED. A stepper value or an editable numeric input. */
  dataStrong: {
    fontFamily: FONT_MONO,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.2,
    ...tnum,
  },
  mono: {
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: '-0.02em',
    ...tnum,
  },
  data: {
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.4,
    ...tnum,
  },
  /** ADDED. A count badge, a week-tile value, a utilisation percentage. */
  monoSmall: {
    fontFamily: FONT_MONO,
    fontSize: 10.5,
    fontWeight: 600,
    lineHeight: 1.2,
    ...tnum,
  },
} as const

export type TokenName = keyof typeof TOKENS
export type TypeStyle = keyof typeof TYPE
